"use client";

import React, {useState} from "react";

import {cn} from "@isudio/utils";
import {Button, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel} from "../../";

interface SearchInputProps {
  className?: string;
  searchRef?: React.RefObject<HTMLInputElement>;
  selectedSearchBy?: string | null;
  onFilterChange?: (payload: {
    type: "searchBy";
    value: string
  }) => void;
  onSearchChange?: (search: string) => void;
}

const SearchInput = React.forwardRef<Element, SearchInputProps>(
  ({
    className,
    searchRef,
    selectedSearchBy = "title",
    onFilterChange,
    onSearchChange,
  }
) => {
    const [isOpen, setIsOpen] = useState(false);

    const searchByOptions = [
      {
        key: "title",
        label: "Title"
      },
      {
        key: "author",
        label: "Author"
      },
      {
        key: "publisher",
        label: "Publisher"
      },
      {
        key: "socialID",
        label: "Uploaded By"
      }
    ];

    const activeSearchByObj = searchByOptions.find(s => s.key === selectedSearchBy) ?? {
      key: "title",
      label: "Title"
    };

    return (
      <div
        className={cn(
          `w-72 flex items-center h-8 bg-bluegray-darker rounded-[25px] px-1 gap-2 border-none focus-within:ring-1 focus-within:ring-bluegray-light/30 transition-all duration-200`,
          className
        )}
      >
        {/* Search Scope Selection Menu */}
        <DropdownMenu
          onOpenChange={setIsOpen}>
          <DropdownMenuTrigger
            asChild>
            <Button
              variant="vanilla"
              className="h-6 px-2 flex items-center gap-1.5 border-none bg-bluegray-light/30 text-xs text-white rounded-[15px] cursor-pointer transition-all duration-200 shrink-0"
            >
              <i className="fa-duotone fa-light fa-magnifying-glass text-xs text-bluegray-lighter"/>
              <span className="text-xs font-medium text-pale-white">{activeSearchByObj.label}</span>
              <i className={`fa-duotone fa-light fa-chevron-${isOpen ? 'up' : 'down'} text-[10px] text-bluegray-lighter`}/>
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            className="w-28 bg-bluegray-darker rounded-[6px] shadow-[1px_1px_2px_1px_rgba(105,120,127,1)]!"
            align="start"
          >
            <DropdownMenuLabel className="bg-bluegray-medium/50 text-bluegray-lighter font-extrabold text-[11px] tracking-wider px-2 py-1">Search By</DropdownMenuLabel>
            {searchByOptions.map((opt) => {
              const isSelected = selectedSearchBy === opt.key;
              return (
                <DropdownMenuItem
                  key={opt.key}
                  onClick={() => onFilterChange?.({
                    type: "searchBy",
                    value: opt.key
                  })}
                  className={`flex justify-between items-center text-xs transition duration-200 cursor-pointer px-2 py-1.5 
                    ${isSelected 
                      ? 'text-white bg-bluegray-light/30' 
                      : 'text-bluegray-lighter hover:text-generic-hover hover:bg-bluegray-light/12'
                  }`}
                >
                  <span>{opt.label}</span>
                  {isSelected &&
                    <i
                      className="fa-duotone fa-light fa-check text-[10px] text-bluegray-lighter"/>}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Main Text Input Area */}
        <input
          ref={searchRef}
          type="text"
          placeholder={`Search ${activeSearchByObj.label.toLowerCase()}...`}
          onChange={onSearchChange ? (e) => onSearchChange(e.target.value) : undefined}
          className="bg-transparent text-white text-xs border-none focus:outline-none focus:ring-0 h-full flex-1 pr-2 pl-0.5 placeholder-bluegray-light"
        />
      </div>
    );
  }
);

SearchInput.displayName = "SearchInput";
export {SearchInput};